import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Hash,
  Copy,
  Check,
  ShieldCheck,
  MessageCircle,
  Mail,
  Landmark,
  Globe2,
  Receipt,
} from "lucide-react";

const BANK_DETAILS = [
  { label: "Beneficiary Name", value: "GAINA THE PRELUDE", icon: Landmark, tid: "bank-beneficiary" },
  { label: "Bank Name", value: "Indian Overseas Bank", icon: Building2, tid: "bank-name" },
  { label: "Branch Address", value: "Ansari Road, Daryaganj (2114)", icon: MapPin, tid: "bank-branch" },
  { label: "Account Number", value: "211402000005269", icon: Hash, tid: "bank-account", mono: true },
  { label: "IFSC Code", value: "IOBA0002114", icon: Hash, tid: "bank-ifsc", mono: true },
  { label: "SWIFT Code", value: "IOBAINBB001", icon: Globe2, tid: "bank-swift", mono: true },
];

export default function PaymentOptions() {
  const [copied, setCopied] = useState("");

  const copy = async (text, tid) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tid);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  return (
    <div className="bg-[#fafaf9] min-h-screen" data-testid="payment-options-page">
      {/* HERO */}
      <section className="bg-[#09090b] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative">
          <div className="inline-flex items-center gap-2 border border-[#ea580c]/40 bg-[#ea580c]/10 text-[#fb923c] px-3 py-1.5 text-xs font-bold tracking-[0.2em] uppercase mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> Payment Options
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] max-w-4xl" data-testid="payment-title">
            Bank Transfer — <span className="text-[#fb923c]">Safe. Reliable.</span> Straightforward.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
            Choose direct bank transfer for a secure payment experience. Below are the account details of <strong className="text-white">GAINA THE PRELUDE</strong>. Once payment is done, share the receipt with us for quick verification.
          </p>
        </div>
      </section>

      {/* BANK CARD */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Bank Details Card */}
          <div className="lg:col-span-3">
            <div className="border-2 border-[#09090b] bg-white" data-testid="bank-card">
              {/* Card Header */}
              <div className="bg-[#09090b] text-white p-6 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#fb923c]">Beneficiary Bank</div>
                  <div className="font-display text-2xl md:text-3xl font-black mt-1">Indian Overseas Bank</div>
                </div>
                <Landmark className="w-10 h-10 text-[#fb923c]" strokeWidth={2} />
              </div>

              {/* Details */}
              <div className="divide-y divide-[#e7e5e4]">
                {BANK_DETAILS.map((d) => {
                  const Icon = d.icon;
                  return (
                    <div key={d.tid} className="p-5 md:p-6 flex items-start gap-4" data-testid={d.tid}>
                      <div className="w-10 h-10 shrink-0 bg-[#fff7ed] border border-[#fed7aa] flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#ea580c]" strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#71717a]">{d.label}</div>
                        <div className={`mt-1 font-black text-[#09090b] break-words ${d.mono ? "font-mono text-lg tracking-wider" : "font-display text-lg md:text-xl"} ${d.muted ? "text-[#a1a1aa] italic font-medium" : ""}`}>
                          {d.value}
                        </div>
                      </div>
                      {!d.muted && (
                        <button
                          onClick={() => copy(d.value, d.tid)}
                          className="shrink-0 border-2 border-[#09090b] hover:bg-[#09090b] hover:text-white px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5"
                          data-testid={`${d.tid}-copy`}
                        >
                          {copied === d.tid ? (<><Check className="w-3.5 h-3.5" /> Copied</>) : (<><Copy className="w-3.5 h-3.5" /> Copy</>)}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* After payment banner */}
            <div className="mt-6 bg-[#fff7ed] border-l-4 border-[#ea580c] p-5 flex gap-4" data-testid="post-payment-note">
              <Receipt className="w-5 h-5 text-[#ea580c] shrink-0 mt-0.5" />
              <div className="text-sm text-[#7c2d12] leading-relaxed">
                <strong className="block mb-1 font-black">After Payment</strong>
                Please send your <strong>payment screenshot or transaction receipt</strong> to us via <strong>Email</strong> or <strong>WhatsApp</strong> for quick order verification and processing. Kindly mention your Order ID in the message.
              </div>
            </div>
          </div>

          {/* Right: Contact CTA */}
          <div className="lg:col-span-2">
            <div className="bg-[#09090b] text-white p-8 md:p-10 h-full flex flex-col" data-testid="payment-contact-card">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#fb923c] mb-3">Share Payment Receipt</div>
              <h3 className="font-display text-2xl md:text-3xl font-black leading-tight">
                Send your receipt to any one of these — we verify and dispatch fast.
              </h3>

              <div className="mt-8 space-y-3">
                <a
                  href="https://wa.me/919953272630?text=Hi%20GAINA%20THE%20PRELUDE%2C%20I%20have%20made%20the%20bank%20transfer.%20Sharing%20the%20receipt."
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white px-5 py-4 font-bold transition-colors"
                  data-testid="payment-whatsapp-btn"
                >
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Receipt
                  </span>
                  <span className="font-mono text-sm">+91 99532 72630</span>
                </a>
                <a
                  href="mailto:gainatheprelude@gmail.com?subject=Payment%20Receipt%20-%20Order%20%23"
                  className="flex items-center justify-between gap-3 border border-white/25 hover:border-white text-white px-5 py-4 font-bold transition-colors"
                  data-testid="payment-email-btn"
                >
                  <span className="inline-flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Receipt
                  </span>
                </a>
              </div>

              <div className="mt-auto pt-8 border-t border-white/10 text-xs text-[#a1a1aa] leading-relaxed">
                <div className="font-bold text-white mb-1">GAINA THE PRELUDE</div>
                New Delhi, India<br />
                Email: gainatheprelude@gmail.com
              </div>
            </div>
          </div>
        </div>

        {/* SAFETY NOTE */}
        <div className="mt-12 border-2 border-[#e7e5e4] bg-white p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="safety-tips">
          <div>
            <div className="w-10 h-10 bg-[#dcfce7] border border-[#bbf7d0] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#166534]" strokeWidth={2.2} />
            </div>
            <div className="mt-4 font-display font-black text-[#09090b]">Safe & Reliable</div>
            <p className="mt-2 text-sm text-[#57534e] leading-relaxed">Direct bank transfer avoids gateway fees and processing charges. Your money goes straight to our verified business account.</p>
          </div>
          <div>
            <div className="w-10 h-10 bg-[#e0e7ff] border border-[#c7d2fe] flex items-center justify-center">
              <Receipt className="w-5 h-5 text-[#3730a3]" strokeWidth={2.2} />
            </div>
            <div className="mt-4 font-display font-black text-[#09090b]">Always Share Receipt</div>
            <p className="mt-2 text-sm text-[#57534e] leading-relaxed">Every transfer must be followed by a WhatsApp or Email confirmation with your Order ID for quick verification and dispatch.</p>
          </div>
          <div>
            <div className="w-10 h-10 bg-[#fef3c7] border border-[#fde68a] flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#92400e]" strokeWidth={2.2} />
            </div>
            <div className="mt-4 font-display font-black text-[#09090b]">We Reply Fast</div>
            <p className="mt-2 text-sm text-[#57534e] leading-relaxed">Our team acknowledges receipts within business hours (Mon–Fri, 10 AM – 6 PM) and shares dispatch details on the same day where possible.</p>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap gap-6 justify-center items-center text-sm">
          <Link to="/shipping-policy" className="inline-flex items-center gap-2 border-b-2 border-[#09090b] text-[#09090b] font-bold tracking-wide uppercase pb-1 hover:text-[#ea580c] hover:border-[#ea580c] transition-colors">
            Shipping &amp; Delivery →
          </Link>
          <Link to="/return-policy" className="inline-flex items-center gap-2 border-b-2 border-[#09090b] text-[#09090b] font-bold tracking-wide uppercase pb-1 hover:text-[#ea580c] hover:border-[#ea580c] transition-colors">
            Return Policy →
          </Link>
          <Link to="/books" className="inline-flex items-center gap-2 border-b-2 border-[#09090b] text-[#09090b] font-bold tracking-wide uppercase pb-1 hover:text-[#ea580c] hover:border-[#ea580c] transition-colors">
            Continue Shopping →
          </Link>
        </div>
      </section>
    </div>
  );
}
